import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {SessionService} from '../../services/session/session.service';
import {ActivatedRoute, Router} from '@angular/router';
// @ts-ignore
import packageInfo from '../../../../package.json';
import {AppSettings} from '../../app.settings';
import {UiAlertService} from '../../services/ui-alert/ui-alert.service';
import {BrandsService} from '../../services/brands/brands.service';
import {CodeInputComponent} from 'angular-code-input';
import {CognitoMfaService} from '../../services/cognito/cognito-mfa.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  @ViewChild('codeInput') codeInput !: CodeInputComponent;

  private static readonly BLOCKED_LOGIN_EMAIL = 'job_runner@ourly.com';
  private static readonly BLOCKED_LOGIN_MESSAGE = 'job_runner@ourly.com can no longer be used to log in.';

  public form: UntypedFormGroup;
  public error = '';
  public info = packageInfo.version;
  public logoUrl = '';
  public isLoginSubmitting = false;
  protected readonly AppSettings = AppSettings;

  public isIntegrationAdmin = AppSettings.isIntegration();
  public isContentAdmin = AppSettings.isContentAdmin();
  public isCustomerSupport = AppSettings.isCustomerSupport();
  public isBrokerAdmin = AppSettings.isBroker();
  public isPayrollAdmin = AppSettings.isPayrollAdmin();
  public canShowPayrollAdminSignup = false;
  hidePassword = true;

  loginWithoutPassword = false;
  emailCodeInput = false;
  resendEmailCodeTimerCounter = 60;
  showResendEmailCodeTimerCounter = false;

  public loginTitle = this.isCustomerSupport ? 'Customer Support Login' :
    this.isIntegrationAdmin ? 'Integration Admin Login' :
    this.isBrokerAdmin ? 'Broker Admin Login' :
    this.isContentAdmin ? 'Content Admin Login' :
    this.isPayrollAdmin ? 'Direct Benefits Admin Login' : 'Team Admin Login';

  constructor(
      private formBuilder: UntypedFormBuilder,
      private sessionService: SessionService,
      private router: Router,
      private route: ActivatedRoute,
      private uiAlertService: UiAlertService,
      private brandsService: BrandsService,
      private cognitoMFAService: CognitoMfaService,
  ) {
    this.form = this.formBuilder.group({
          username: new UntypedFormControl('', [Validators.required]),
          password: new UntypedFormControl('', [Validators.required])
        }
    );
  }

  async ngOnInit(): Promise<void> {
    this.brandsService.init().then(() => {
      this.logoUrl = this.brandsService.getLogoOnWhite();
      this.route.params.subscribe(params => {
        if (params.showTimeout === 'true') {
          this.uiAlertService.presentMessage('You have been logged out due to inactivity');
          this.router.navigate(['login']);
        }
      });
    });
  }

  async loginClicked(): Promise<void> {
    if (this.isLoginSubmitting) {
      return;
    }

    this.error = '';
    if (this.isBlockedLoginEmail()) {
      this.error = LoginComponent.BLOCKED_LOGIN_MESSAGE;
      return;
    }

    this.isLoginSubmitting = true;
    try {
      await this.sessionService.loginUserPassword(this.form.get('username')?.value, this.form.get('password')?.value);
      await this.sessionService.navigateAfterLogin();
    } catch (error: any) {
      console.error('Error in login method', error);
      this.error = this.isIntegrationAdmin ? error?.error?.errors[0]?.message : error?.error?.errors;
      this.isLoginSubmitting = false;
    }
  }

  async sendEmail(): Promise<void> {
    this.error = '';
    if (this.isBlockedLoginEmail()) {
      this.error = LoginComponent.BLOCKED_LOGIN_MESSAGE;
      return;
    }

    const cognitoUserResponse: any = await this.cognitoMFAService.cognitoMFASignInWithoutPassword(this.form.get('username')?.value);
    try {
      if (cognitoUserResponse && cognitoUserResponse.challengeName === 'CUSTOM_CHALLENGE') {
        this.emailCodeInput = true;
      }
    } catch (error) {
      this.error = `Error in send email ${error}`;
    }
  }

  async onCodeCompleted(c: string): Promise<void> {
    try {
      if (this.isBlockedLoginEmail()) {
        this.error = LoginComponent.BLOCKED_LOGIN_MESSAGE;
        this.codeInput.reset();
        return;
      }
      return await this.sessionService.verifyEmailCode(
          this.form.get('username')?.value, c
      );
    } catch (error) {
      this.error = 'The code is incorrect';
      this.codeInput.reset();
      setTimeout(() => this.error = '', 3000);
    }
  }

  resendEmailCodeTimer(): void {
    this.resendEmailCodeTimerCounter = 60;
    this.showResendEmailCodeTimerCounter = true;
    const interval = setInterval(() => {
      this.resendEmailCodeTimerCounter--;

      if (this.resendEmailCodeTimerCounter <= 0) {
        clearInterval(interval);
        this.showResendEmailCodeTimerCounter = false;
      }
    }, 1000);
  }

  private isBlockedLoginEmail(): boolean {
    return `${this.form.get('username')?.value || ''}`.trim().toLowerCase() === LoginComponent.BLOCKED_LOGIN_EMAIL;
  }
}
